import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { evidence, custodyLog } from '@/data';
import { PageHeader } from '@/components/layout/PageHeader';
import { Btn } from '@/components/ui/Button';
import { SectionCard } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { FieldGrid } from '@/components/ui/FieldGrid';

export default function Evidence() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ln = (ko: string, mn: string) => (lang === 'ko' ? ko : mn);
  const [cat, setCat] = useState('all');
  const [status, setStatus] = useState('all');
  const [selectedRow, setSelectedRow] = useState(evidence[0].no);

  const statusTone: Record<string, 'info' | 'warning' | 'danger' | 'neutral'> = {
    stInStorage: 'info',
    stAnalysis: 'warning',
    stDisposalReq: 'danger',
    stTransferred: 'neutral',
  };

  const rows = evidence.filter(
    (e) => (cat === 'all' || e.cat === cat) && (status === 'all' || e.status === status),
  );

  const counts = {
    total: evidence.length,
    inStorage: evidence.filter((e) => e.status === 'stInStorage').length,
    analysis: evidence.filter((e) => e.status === 'stAnalysis').length,
    disposal: evidence.filter((e) => e.status === 'stDisposalReq').length,
  };

  const sel = evidence.find((e) => e.no === selectedRow);

  return (
    <div className="mp-page" data-screen-label="증거물 관리">
      <PageHeader
        crumbs={[t('home'), t('mInvest'), t('mEvidence')]}
        title={t('evidenceListTitle')}
        actions={
          <Fragment>
            <Btn icon="bi-file-earmark-excel">{t('excel')}</Btn>
            <Btn kind="primary" icon="bi-box-arrow-in-down">{t('registerEvidence')}</Btn>
          </Fragment>
        }
      />

      <div className="mp-summary">
        <div className="mp-summary__item">
          <span className="mp-summary__label">{t('summaryTotal')}</span>
          <span className="mp-summary__num">{counts.total}<small>{t('items')}</small></span>
        </div>
        <div className="mp-summary__item">
          <span className="mp-summary__label">{t('summaryInStorage')}</span>
          <span className="mp-summary__num">{counts.inStorage}<small>{t('items')}</small></span>
        </div>
        <div className="mp-summary__item">
          <span className="mp-summary__label">{t('summaryAnalysis')}</span>
          <span className="mp-summary__num mp-summary__num--warn">{counts.analysis}<small>{t('items')}</small></span>
        </div>
        <div className="mp-summary__item">
          <span className="mp-summary__label">{t('summaryDisposal')}</span>
          <span className="mp-summary__num mp-summary__num--danger">{counts.disposal}<small>{t('items')}</small></span>
        </div>
      </div>

      <div className="mp-filterbar">
        <label className="mp-filter">
          <span>{t('filterCategory')}</span>
          <select value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="all">{t('all')}</option>
            <option value="catDrug">{t('catDrug')}</option>
            <option value="catDigital">{t('catDigital')}</option>
            <option value="catCash">{t('catCash')}</option>
            <option value="catOther">{t('catOther')}</option>
          </select>
        </label>
        <label className="mp-filter">
          <span>{t('filterStatus')}</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">{t('all')}</option>
            <option value="stInStorage">{t('stInStorage')}</option>
            <option value="stAnalysis">{t('stAnalysis')}</option>
            <option value="stDisposalReq">{t('stDisposalReq')}</option>
            <option value="stTransferred">{t('stTransferred')}</option>
          </select>
        </label>
        <label className="mp-filter">
          <span>{t('filterCase')}</span>
          <input type="text" placeholder="ХД-2026-" />
        </label>
        <Btn kind="primary" small icon="bi-search">{t('search')}</Btn>
        <Btn small icon="bi-arrow-counterclockwise" onClick={() => { setCat('all'); setStatus('all'); }}>{t('reset')}</Btn>
        <span className="mp-filterbar__count">{t('total')} <strong>{rows.length}</strong> {t('items')}</span>
      </div>

      <div className="mp-grid mp-grid--list-side">
        <SectionCard flush>
          <table className="mp-table mp-table--select">
            <thead>
              <tr>
                <th>{t('evidenceNo')}</th>
                <th>{t('evidenceName')}</th>
                <th>{t('evidenceCategory')}</th>
                <th>{t('weight')}</th>
                <th>{t('relatedCase')}</th>
                <th>{t('storageLocation')}</th>
                <th>{t('storageStatus')}</th>
                <th>{t('registeredDate')}</th>
                <th>{t('registrant')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr
                  key={e.no}
                  className={selectedRow === e.no ? 'is-selected' : ''}
                  onClick={() => setSelectedRow(e.no)}
                >
                  <td><span className="mp-caseno">{e.no}</span></td>
                  <td className="mp-table__strong">{ln(e.nameKo, e.nameMn)}</td>
                  <td>{t(e.cat)}</td>
                  <td>{e.weight}</td>
                  <td><a href="#" onClick={(ev) => ev.preventDefault()}>{e.case}</a></td>
                  <td>{e.loc}</td>
                  <td><Badge tone={statusTone[e.status]}>{t(e.status)}</Badge></td>
                  <td>{e.date}</td>
                  <td>{e.reg}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mp-pagination">
            <button type="button"><i className="bi bi-chevron-left"></i></button>
            <button type="button" className="is-current">1</button>
            <button type="button">2</button>
            <button type="button">3</button>
            <button type="button"><i className="bi bi-chevron-right"></i></button>
          </div>
        </SectionCard>

        <div className="mp-stack">
          <SectionCard title={t('detail')}>
            {sel ? (
              <div className="mp-stack mp-stack--tight">
                <div className="mp-nodedetail__name">{ln(sel.nameKo, sel.nameMn)}</div>
                <FieldGrid rows={[
                  [t('evidenceNo'), <span className="mp-caseno">{sel.no}</span>],
                  [t('relatedCase'), sel.case],
                  [t('weight'), sel.weight],
                  [t('storageLocation'), sel.loc],
                ]} />
                <div className="mp-btnrow">
                  <Btn small icon="bi-arrow-left-right">{t('requestDisposal')}</Btn>
                  <Btn small icon="bi-printer">{t('print')}</Btn>
                </div>
              </div>
            ) : null}
          </SectionCard>
          <SectionCard title={t('chainOfCustody')} flush>
            <ol className="mp-timeline">
              {custodyLog.map((l, i) => (
                <li key={i} className={i === custodyLog.length - 1 ? 'is-last' : ''}>
                  <span className="mp-timeline__date">{l.date}</span>
                  <span className="mp-timeline__text">{ln(l.ko, l.mn)}</span>
                  <span className="mp-timeline__by">{l.by}</span>
                </li>
              ))}
            </ol>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
